/** Evidence intake: upload straight to storage, register, process, recognition summary. */
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";

import { api } from "../api/client";
import { keys, useBatch, useBatches, useMe, useProject } from "../api/hooks";
import type { Batch, Manifest } from "../api/types";
import { can } from "../components/AppShell";
import { StageIntro } from "../components/sat";
import { ErrorNotice, FileDrop, Notice, Spinner } from "../components/ui";
import { uploadFile } from "../lib/files";
import { bytes, dateTime, localText } from "../lib/format";

const ACCEPT = ".zip,.rar,.tar,.gz,.tgz,.csv,.xlsx";

export function RecognitionSummary({ manifest }: { manifest: Manifest }) {
  return (
    <div className="stack">
      <div className="grid-4">
        <div className="metric">
          <span>Batteries</span>
          <strong>{manifest.batteries}</strong>
          <small>ESS telemetry</small>
        </div>
        <div className="metric">
          <span>Dispatch sources</span>
          <strong>{manifest.dispatch_sources.length}</strong>
          <small>PPC / grid dispatch (optional)</small>
        </div>
        <div className="metric">
          <span>Alarm records</span>
          <strong>{manifest.alarm_records.toLocaleString("en-GB")}</strong>
          <small>{manifest.alarm_history_uploaded ? "Alarm history" : "Not uploaded (optional)"}</small>
        </div>
        <div className="metric">
          <span>Data range</span>
          <strong style={{ fontSize: "1rem" }}>
            {manifest.data_range ? localText(manifest.data_range.start_local) : "—"}
          </strong>
          <small>{manifest.data_range ? `to ${localText(manifest.data_range.end_local)} (${manifest.timezone})` : "No telemetry"}</small>
        </div>
      </div>
      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              <th>Uploaded file</th>
              <th>Detected content</th>
              <th>Contained files</th>
            </tr>
          </thead>
          <tbody>
            {manifest.files.map((file, index) => (
              <tr key={`${file.sha256}-${index}`}>
                <td>{file["Uploaded file"]}</td>
                <td>{file["Detected content"]}</td>
                <td className="num">{file["Contained files"]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {manifest.detection && (
        <Notice kind={manifest.detection.auto_detected ? "success" : "info"} title={manifest.detection.auto_detected ? "SAT window detected" : "No SAT window detected"}>
          {manifest.detection.auto_detected
            ? `${localText(manifest.detection.start_local)} – ${localText(manifest.detection.end_local)} (${manifest.timezone}), ${manifest.detection.required_cycles} full cycle(s). You can change it in the test setup.`
            : "The whole data range is proposed as the window. Set the SAT window in the test setup."}
        </Notice>
      )}
      {manifest.warnings.map((warning) => (
        <Notice kind="warning" key={warning}>
          {warning}
        </Notice>
      ))}
    </div>
  );
}

export function EvidencePage() {
  const { pid = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const bid = params.get("b") ?? undefined;
  const me = useMe();
  const project = useProject(pid);
  const editor = can(me.data, "evidence.upload");
  const batch = useBatch(bid);
  const batches = useBatches(pid, editor);
  const client = useQueryClient();
  const navigate = useNavigate();
  const [progress, setProgress] = useState<string | null>(null);
  const [notes, setNotes] = useState<string[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [processing, setProcessing] = useState(false);

  if (me.data && !editor) return <Notice kind="info">Evidence is uploaded by the project team. Open the programme to see the saved results.</Notice>;

  async function add(files: File[]) {
    if (!files.length) return;
    setError(null);
    setNotes([]);
    try {
      let batchId = bid;
      if (!batchId) {
        setProgress("Preparing the evidence batch…");
        const created = await api.post<Batch>(`/projects/${pid}/batches`);
        batchId = created.id;
        setParams({ b: batchId }, { replace: true });
      }
      const collected: string[] = [];
      for (const file of files) {
        const uploaded = await uploadFile(`/projects/${pid}/uploads`, file, "evidence", { batch_id: batchId }, setProgress);
        const registered = await api.post<Batch>(`/batches/${batchId}/files`, { sha256: uploaded.sha256, filename: file.name });
        collected.push(...(registered.notes ?? []));
        client.setQueryData(keys.batch(batchId), (current: Batch | undefined) => ({ ...(current ?? {}), ...registered, manifest: current?.manifest ?? null }));
      }
      setNotes(collected);
      await client.invalidateQueries({ queryKey: keys.batch(batchId) });
      await client.invalidateQueries({ queryKey: keys.batches(pid) });
    } catch (reason) {
      setError(reason);
    } finally {
      setProgress(null);
    }
  }

  async function remove(sha256: string) {
    setError(null);
    try {
      await api.delete(`/batches/${bid}/files/${sha256}`);
      await client.invalidateQueries({ queryKey: keys.batch(bid!) });
      await client.invalidateQueries({ queryKey: keys.batches(pid) });
    } catch (reason) {
      setError(reason);
    }
  }

  async function process() {
    setError(null);
    setProcessing(true);
    try {
      const processed = await api.post<Batch>(`/batches/${bid}/process`);
      client.setQueryData(keys.batch(bid!), processed);
      await client.invalidateQueries({ queryKey: keys.batches(pid) });
    } catch (reason) {
      setError(reason);
    } finally {
      setProcessing(false);
    }
  }

  const files = (batch.data?.files ?? []) as { sha256: string; filename: string; size: number }[];
  const manifest = batch.data?.manifest as Manifest | null | undefined;
  // Files added after the last processing make the summary stale.
  const stale = !!manifest && manifest.files.length !== files.length;
  const timeZone = project.data?.timezone ?? "UTC";
  const others = (batches.data ?? []).filter((item) => item.id !== bid);

  return (
    <div className="stack" style={{ gap: "1.4rem" }}>
      <StageIntro
        title="Add the SAT evidence"
        description="Upload the exports as they come from the site. Files are recognised by their contents, not by their names."
        status={files.length ? `${files.length} file(s)` : "No files yet"}
      />
      <div className="grid-3">
        <div className="card tight">
          <strong>ESS telemetry</strong> <span className="badge review">Required</span>
          <p className="muted small" style={{ margin: "0.3rem 0 0" }}>Battery exports with SOC, power and energy counters.</p>
        </div>
        <div className="card tight">
          <strong>PPC / grid dispatch</strong> <span className="badge neutral">Optional</span>
          <p className="muted small" style={{ margin: "0.3rem 0 0" }}>Adds the commanded power to the charts and the PPC plan.</p>
        </div>
        <div className="card tight">
          <strong>Alarm history</strong> <span className="badge neutral">Optional</span>
          <p className="muted small" style={{ margin: "0.3rem 0 0" }}>Enables the alarm impact review.</p>
        </div>
      </div>
      <FileDrop
        multiple
        accept={ACCEPT}
        disabled={!!progress || processing}
        onFiles={add}
        label={
          progress ? (
            <Spinner label={progress.replace(/…$/, "")} />
          ) : (
            <span>
              <strong>Drop files or archives here</strong>
              <br />
              <span className="muted small">CSV, Excel, ZIP, RAR, TAR.GZ, TGZ, GZ or TAR exports. Large files upload directly to storage.</span>
            </span>
          )
        }
      />
      {notes.map((note) => (
        <Notice kind="info" key={note}>
          {note}
        </Notice>
      ))}
      <ErrorNotice error={error ?? batch.error} />
      {bid && batch.isLoading && <Spinner label="Loading the batch" />}
      {files.length > 0 && (
        <section className="card stack">
          <div className="card-head">
            <div>
              <h3>Files in this batch</h3>
              <p>Raw files are stored unchanged and stay attached to the saved result.</p>
            </div>
          </div>
          <ul className="file-list">
            {files.map((file) => (
              <li key={file.sha256}>
                <span>{file.filename}</span>
                <span className="row">
                  <span className="muted small">{bytes(file.size)}</span>
                  <button className="btn small ghost" disabled={processing} onClick={() => remove(file.sha256)} aria-label={`Remove ${file.filename}`}>
                    Remove
                  </button>
                </span>
              </li>
            ))}
          </ul>
          <div className="row end">
            {processing && <Spinner label="Reading the files and detecting the SAT window" />}
            <button className="btn primary" disabled={processing || !!progress} onClick={process}>
              {manifest ? "Process again" : "Process evidence"}
            </button>
          </div>
        </section>
      )}
      {manifest && (
        <section className="stack">
          <h3>Recognition summary</h3>
          {stale && <Notice kind="warning">The files changed after the last processing. Process the evidence again before continuing.</Notice>}
          <RecognitionSummary manifest={manifest} />
          <div className="row end">
            <button className="btn primary" disabled={stale || manifest.batteries === 0} onClick={() => navigate(`/p/${pid}/batches/${bid}/setup`)}>
              Continue to test setup →
            </button>
          </div>
        </section>
      )}
      {others.length > 0 && (
        <details className="disclosure">
          <summary>Earlier evidence batches ({others.length})</summary>
          <div>
            <ul className="file-list">
              {others.map((item) => (
                <li key={item.id}>
                  <span>
                    {item.files} file(s) · {dateTime(item.created_at, timeZone)}
                    <span className="muted small"> · {item.processed ? "processed" : "not processed"}</span>
                  </span>
                  <span className="row">
                    <Link className="btn small" to={`/p/${pid}/evidence?b=${item.id}`}>
                      Open
                    </Link>
                    {item.processed ? (
                      <Link className="btn small" to={`/p/${pid}/batches/${item.id}/setup`}>
                        Test setup
                      </Link>
                    ) : null}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </details>
      )}
    </div>
  );
}
